/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { invoiceService } from "@src/@api-services/invoice";
import type { GetInvoicesParams } from "@src/@api-services/invoice";
import { SortOrder } from "@src/shared/constants/sort-order.constants";
import type { Invoice } from "@src/views/apps/financial/types";

interface InvoicesState {
  data: Invoice[];
  total: number;
  params: GetInvoicesParams;
  selectedInvoice: Invoice | null;
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
}

const mapInvoiceRow = (row: any): Invoice => ({
  id: String(row.id),
  invoiceNumber: row.invoiceNumber ?? null,
  transactionId: String(row.transactionId || row.orderId || row.id),
  recipientType: row.recipientType ?? null,
  recipientName: row.recipientName ?? row.userName ?? null,
  recipientEmail: row.recipientEmail ?? row.userEmail ?? "",
  amount: Number(row.amount ?? row.totalAmount ?? 0),
  currency: row.currency ?? "",
  status: row.status ?? null,
  issuedAt: row.issuedAt ?? row.createdAt ?? "",
  dueAt: row.dueAt,
  pdfAvailable: Boolean(row.pdfAvailable),
});

export const getInvoices = createAsyncThunk(
  "invoices/getInvoices",
  async (params: GetInvoicesParams, { rejectWithValue }) => {
    try {
      const response = await invoiceService.getInvoices(params);
      const payload = response.data?.data ?? response.data ?? {};
      const apiData = Array.isArray(payload?.data) ? payload.data : [];
      const apiTotal = Number(payload?.totalRecord ?? apiData.length ?? 0);
      return {
        params,
        data: apiData.map(mapInvoiceRow),
        total: apiTotal,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch invoices"
      );
    }
  }
);

export const getInvoice = createAsyncThunk(
  "invoices/getInvoice",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await invoiceService.getInvoice(id);
      const row = response.data?.data ?? response.data;
      return row ? mapInvoiceRow(row) : null;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch invoice"
      );
    }
  }
);

const initialState: InvoicesState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "issuedAt",
    orderDirection: SortOrder.DESC,
  },
  selectedInvoice: null,
  loading: false,
  detailLoading: false,
  error: null,
};

export const invoicesSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedInvoice: (state) => {
      state.selectedInvoice = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getInvoice.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getInvoice.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedInvoice = action.payload;
      })
      .addCase(getInvoice.rejected, (state) => {
        state.detailLoading = false;
        state.selectedInvoice = null;
      });
  },
});

export const { clearError, clearSelectedInvoice } = invoicesSlice.actions;
export default invoicesSlice.reducer;
